import React, { useState, useEffect, useCallback } from "react";
import { FiEdit2, FiPlus, FiTrash2, FiCheckCircle } from "react-icons/fi";
import API from "../../utils/api";
import toast from "react-hot-toast";
import OpeningBalanceModal from "./OpeningBalanceModal";
import SettleOpeningBalanceModal from "./SettleOpeningBalanceModal";

/**
 * OpeningBalanceCard — "Opening Balance" section on the business profile.
 *
 *  - Business not approved  → disabled, asks to approve first
 *  - Approved, none set     → "Add" button
 *  - Set                    → amount / type / date with Edit and Delete
 *  - No edit permission     → read-only (no buttons)
 *  - Debit balance pending  → shows Settled / Pending and a "Settle" button
 *                             (records a receipt against the opening balance)
 *
 * Props
 *   business   – selected business (opening_balance, _type, _date, id, companyName)
 *   isVerified – live approval state of the business
 *   canEdit    – admin, or staff with "businesses:edit"
 *   onChanged  – called with the updated business after add / edit / delete
 */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) =>
  d
    ? new Date(String(d).slice(0, 10) + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

const btn = {
  display: "flex", alignItems: "center", gap: 5, padding: "4px 12px",
  border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer",
  fontSize: "0.78rem", background: "#fff", fontFamily: "inherit",
};

const OpeningBalanceCard = ({ business, isVerified, canEdit, onChanged }) => {
  const [showModal, setShowModal] = useState(false);
  const [showSettle, setShowSettle] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [summary, setSummary] = useState(null);   // { amount, settled, remaining, canSettle, ... }

  const loadSummary = useCallback(async () => {
    if (!business?.id) return;
    try {
      const { data } = await API.get(`/business/${business.id}/opening-balance`);
      setSummary(data.openingBalance || null);
    } catch {
      setSummary(null); // card still works without the settlement figures
    }
  }, [business?.id]);

  // reload whenever the balance itself changes (add / edit / delete)
  useEffect(() => { loadSummary(); },
    [loadSummary, business?.opening_balance, business?.opening_balance_type, business?.opening_balance_date]);

  const amount = Number(business?.opening_balance || 0);
  const hasOb = amount > 0;
  const isDebit = (business?.opening_balance_type || "debit") === "debit";

  const handleDelete = async () => {
    if (!window.confirm(`Delete the opening balance of ${money(amount)} for ${business.companyName}?`)) return;
    try {
      setDeleting(true);
      const { data } = await API.delete(`/business/${business.id}/opening-balance`);
      toast.success("Opening balance deleted");
      onChanged && onChanged(data.business);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete opening balance");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "22px 24px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "var(--primary)", fontSize: "1rem" }}>📒</span>
          <span style={{ fontWeight: 700, fontSize: "0.92rem", color: "#111" }}>Opening Balance</span>
        </div>

        {canEdit && isVerified && (
          <div style={{ display: "flex", gap: 6 }}>
            {hasOb && summary?.canSettle && (
              <button onClick={() => setShowSettle(true)} style={{ ...btn, background: "#16a34a", color: "#fff", border: "none" }}>
                <FiCheckCircle size={12} /> Settle
              </button>
            )}
            {!hasOb ? (
              <button onClick={() => setShowModal(true)} style={{ ...btn, background: "var(--primary)", color: "#fff", border: "none" }}>
                <FiPlus size={12} /> Add
              </button>
            ) : (
              <>
                <button onClick={() => setShowModal(true)} style={btn}><FiEdit2 size={12} /> Edit</button>
                <button onClick={handleDelete} disabled={deleting} style={{ ...btn, color: "#dc2626", borderColor: "#fecaca" }}>
                  <FiTrash2 size={12} /> {deleting ? "Deleting…" : "Delete"}
                </button>
              </>
            )}
          </div>
        )}

        {/* Approved-state edge case: balance exists but business was un-approved later → delete still allowed */}
        {canEdit && !isVerified && hasOb && (
          <button onClick={handleDelete} disabled={deleting} style={{ ...btn, color: "#dc2626", borderColor: "#fecaca" }}>
            <FiTrash2 size={12} /> {deleting ? "Deleting…" : "Delete"}
          </button>
        )}
      </div>

      {/* Body */}
      {!isVerified && !hasOb ? (
        <div style={{ fontSize: "0.86rem", color: "var(--text-muted)" }}>
          🔒 Approve this business to add an opening balance.
        </div>
      ) : !hasOb ? (
        <div style={{ fontSize: "0.86rem", color: "var(--text-muted)" }}>
          No opening balance set.{canEdit ? " Use Add to enter the balance this customer had before the system started." : ""}
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <div className="biz-stat-chip">
              <div className="biz-stat-chip-label">Amount</div>
              <div className="biz-stat-chip-value" style={{ color: isDebit ? "#ef4444" : "#059669" }}>{money(amount)}</div>
            </div>
            <div className="biz-stat-chip">
              <div className="biz-stat-chip-label">Type</div>
              <div className="biz-stat-chip-value" style={{ fontSize: "0.9rem" }}>
                {isDebit ? "They owe us (Debit)" : "Advance / we owe them (Credit)"}
              </div>
            </div>
            <div className="biz-stat-chip">
              <div className="biz-stat-chip-label">As-on Date</div>
              <div className="biz-stat-chip-value" style={{ fontSize: "0.9rem" }}>{fmtDate(business.opening_balance_date)}</div>
            </div>
          </div>
          {isDebit && summary && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginTop: 10 }}>
              <div className="biz-stat-chip">
                <div className="biz-stat-chip-label">Settled</div>
                <div className="biz-stat-chip-value" style={{ color: "#059669" }}>{money(summary.settled)}</div>
              </div>
              <div className="biz-stat-chip">
                <div className="biz-stat-chip-label">Pending</div>
                <div className="biz-stat-chip-value" style={{ color: summary.remaining > 0 ? "#ef4444" : "#059669" }}>
                  {summary.remaining > 0 ? money(summary.remaining) : "Fully settled ✓"}
                </div>
              </div>
            </div>
          )}
          {isDebit && summary?.settled > 0 && (
            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 8 }}>
              Part of this balance has been settled by receipts, so its type and date are locked and it can't be deleted until those receipts are reversed.
            </div>
          )}
          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 10 }}>
            This is the first row of the customer ledger. Bills and receipts dated before the as-on date are not shown there.
          </div>
        </>
      )}

      {showModal && (
        <OpeningBalanceModal
          business={business}
          onClose={() => setShowModal(false)}
          onSaved={(updated) => onChanged && onChanged(updated)}
        />
      )}

      {showSettle && summary && (
        <SettleOpeningBalanceModal
          business={business}
          summary={summary}
          onClose={() => setShowSettle(false)}
          onSaved={() => { loadSummary(); }}
        />
      )}
    </div>
  );
};

export default OpeningBalanceCard;
